import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import SearchInput from "../../../../components/base/input/search_input";
import {Tab, Tabs} from "../../../../components/base/Tabs";
import Button from "../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import SortableTaskColumns from "./sortable-task-columns";
import IconButton from "../../../../components/base/button/icon_button";
import {ReactComponent as SortIcon} from '../../../../../assets/images/sort-icon.svg';
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import Api from "../../../../../core/services/api/api";
import {dateComparator, numComparator} from "../../../../../core/services/utils/utils";
import UpsertTaskStatusesDialog from "../../../../components/dialogs/upsert-task-statuses";
import {TaskStatusTypes} from "../../../../../core/constants/enums";
import useSearch from "../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import {useOperatorInformation} from "../../../../hooks/use-operator-information";

const tabs = {
    myTasks: 'myTasks',
    assignedTasks: 'assignedTasks',
}

const tabsList = [
    {title: 'My Tasks', value: tabs.myTasks},
    {title: 'Assigned Tasks', value: tabs.assignedTasks},
]

const defaultTab = tabs.myTasks;
const sortableGroupName = 'tasks';
const initialStatusesDialogState = false;

const NotebookView = () => {
    const [statuses, setStatuses] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [sortStates, setSortStates] = useState({});
    const dispatch = useDispatch();
    const [movingATask, setMovingATask] = useState(false);
    const [statusesDialogState, setStatusesDialogState] = useState(initialStatusesDialogState)
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('notebook-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorInformation = useOperatorInformation();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.notebook);
        configureBehavioralTool((tags) => tags.notebook);
    }, [configureBehavioralTool, configureNotebookTags]);

    const taskGroups = useMemo(() => {
        const result = [];
        for (const status of statuses) {
            const statusTasks = tasks.filter(t => t.taskStatus.id === status.id);
            const sortState = sortStates[status.id];
            switch (sortState) {
                case 1:
                    statusTasks.sort((a, b) => dateComparator(a.createdDateTime, b.createdDateTime));
                    break;
                case -1:
                    statusTasks.sort((a, b) => numComparator(b.createdDateTime, a.createdDateTime));
                    break;
                default:
                    break;
            }
            result.push({
                status: status,
                tasks: statusTasks,
            })
        }
        return result;
    }, [sortStates, statuses, tasks]);

    /**
     * Fetches the task statuses from the api
     * @type {(function(): Promise<void>)}
     */
    const getStatuses = useCallback(async () => {
        const response = await Api.getTaskStatuses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const statuses = response?.data?.sort((a, b) => numComparator(a.orderIndex, b.orderIndex)) ?? [];
            setStatuses(statuses);
            setSortStates(Object.fromEntries(statuses.map(s => [s.id, 0])))
        }
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - fetches the statuses from the api
     */
    useEffect(() => {
        getStatuses().then();
    }, [getStatuses]);

    /**
     * Searches over the tasks of the current user in the notebook.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        /**@type{ApiResponseDs}*/
        let response;
        const forApi = {
            keyword: args.filters?.keyword,
        }
        switch (args.tab) {
            case tabs.myTasks:
            default:
                response = await Api.getMyTasks(forApi, args.cancelTokenSource?.token);
                break;
            case tabs.assignedTasks:
                response = await Api.getAssignedTasks(forApi, args.cancelTokenSource?.token);
                break;
        }
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTasks(response?.data ?? []);
        }
    }, [isMounted]);

    const initialSearchDataSettings = useMemo(() => ({
        tab: defaultTab,
    }), []);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Searches over the application suggestions based on the received keyword
     * @param keyword
     */
    const searchOverRecords = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword,
        });
    }, [searchData])

    /**
     * Handles changes in the selected tab for status of the suggested application records
     * @param newValue
     */
    const onTabChanged = useCallback((e, newValue) => {
        searchData.setTab(newValue);
    }, [searchData]);

    /**
     * Adds the received task to the list of tasks
     * @param task
     */
    const handleAddingNewTask = useCallback((task) => {
        if (!isMounted())
            return;
        if (searchData.tab === tabs.myTasks && task.createdByOperator?.id !== operatorInformation?.id)
            return;
        setTasks(prevState => {
            // if task exists, then merge otherwise add
            const index = prevState.findIndex(e => e.id === task.id);
            if (index === -1)
                return [task, ...prevState];
            prevState[index] = task;
            return [...prevState];
        });
    }, [isMounted, operatorInformation?.id, searchData.tab])

    /**
     * Handles adding a new task to the list:
     * Dispatches opening the upsert task dialog with initial state
     */
    const onAddNewTaskClicked = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                onSuccess: handleAddingNewTask,
                task: null,
            }))
    }, [dispatch, handleAddingNewTask])

    /**
     * @type {function(SortableEvent): void}
     */
    const onNoteDragged = useCallback(async (event) => {
        const {oldDraggableIndex, from, to} = event;
        if (from === to)
            return;

        const statusOfFrom = statuses.find(s => `task-status-${s.id}` === from.id);
        const statusOfTo = statuses.find(s => `task-status-${s.id}` === to.id);

        const task = taskGroups.find(g => g.status.id === statusOfFrom.id)?.tasks[oldDraggableIndex];
        if (!task)
            return;

        const forApi = {
            taskId: task.id,
            taskStatusId: statusOfTo.id,
        }
        setMovingATask(true);
        setTasks(prevState => prevState.map(e => e.id === task.id ? {...e, taskStatus: statusOfTo} : e));
        const response = await Api.changeTaskStatus(forApi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setTasks(prevState => prevState.map(e => e.id === task.id ? {...e, taskStatus: statusOfFrom} : e));
        }
        setMovingATask(false);
    }, [isMounted, statuses, taskGroups]);

    /**
     * Toggles the task done status.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleTaskDoneStatus = useCallback(async (task, done) => {
        const forApi = {
            taskId: task?.id,
            taskStatusId: done ? statuses.find(e => e.statusType === TaskStatusTypes.Done)?.id : statuses.find(e => e.statusType === TaskStatusTypes.ToDo)?.id,
        }
        const response = await Api.changeTaskStatus(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTasks(prevState => prevState.map(e => e.id === task.id ? {...e, taskStatus: response?.data} : e));
        }
        return response;
    }, [isMounted, statuses])

    /**
     * Opens the upsert task dialog with the received task
     * @param task
     * @param close
     */
    const onEditTask = useCallback((task, close) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                onSuccess: handleAddingNewTask,
                task: task,
            }))
        close();
    }, [dispatch, handleAddingNewTask]);

    /**
     * Removes the received task from the list of tasks
     * @param task
     * @param close
     * @return {Promise<void>}
     */
    const onRemoveTask = useCallback(async (task, close) => {
        close();
        const response = await Api.removeTask(task.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTasks(prevState => prevState.filter(e => e.id !== task.id));
        }
    }, [isMounted])

    /**
     * Changes the sort status of the received status
     * @type {(function(*): void)}
     */
    const changeSortStatus = useCallback((status) => {
        setSortStates(prevState => ({
            ...prevState,
            [status.id]: prevState[status.id] === 1 ? -1 : prevState[status.id] + 1
        }));
    }, []);

    /**
     * Opens the statuses dialog
     * @type {(function(): void)}
     */
    const openStatusesDialog = useCallback(() => {
        setStatusesDialogState(true);
    }, []);


    /**
     * Sets the task with the received [taskId] to the received [callbackOrTask]
     * @type {(function(number, function | any): void)}
     */
    const setTask = useCallback((taskId, callbackOrTask) => {
        setTasks(prevState => {
            if (typeof callbackOrTask === 'function') {
                const found = prevState.find(e => e.id === taskId);
                if (!found)
                    return prevState;
                return prevState.map(e => e.id === taskId ? callbackOrTask(found) : e);
            }
            return prevState.map(e => e.id === taskId ? callbackOrTask : e);
        })
    }, [])

    return (
        <>
            <div className={'note-book-view'}>
                <div className={'d-flex justify-content-between align-items-center w-100 mt-4 mb-4'}>
                    <h2 className={'text-xl font-weight-semi-bold '}>
                        Notebook
                    </h2>
                    <div className={'d-flex align-items-center justify-content-end'}>
                        <Button
                            className='justify-content-center align-items-center d-flex'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={openStatusesDialog}
                        >
                            Manage Statuses
                        </Button>
                        <Button
                            className='justify-content-center align-items-center d-flex ml-2'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={onAddNewTaskClicked}
                        >
                            <Add className='icon small mr-2'/>
                            <p className={'button-label'}>Add a new task</p>
                        </Button>
                    </div>
                </div>
                <SearchInput
                    initialValue={searchData.filters?.keyword}
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchOverRecords}
                    label={'Search'}
                />
                <Tabs
                    className={'mt-4'}
                    value={searchData.tab ?? false}
                    onChange={onTabChanged}
                >
                    {
                        tabsList?.map(e => (
                            <Tab key={e.value} label={e.title} value={e.value}/>
                        ))
                    }
                </Tabs>
                <div className={'mb-4'}/>
                <div className={'notebook-lists-container'}>
                    <div className={'notebook-lists'}>
                        {
                            taskGroups.map(g => (
                                <div key={g.status.id} className={'task-list-column'}>
                                    <div className={'column-header'}>
                                        <p>
                                            {
                                                g.status.title
                                            }
                                        </p>
                                        <IconButton
                                            color={'paper.secondary'}
                                            className={'sort-button'}
                                            onClick={() => changeSortStatus(g.status)}
                                            disabled={searchData.loading}
                                        >
                                            {
                                                sortStates[g.status.id] === 0
                                                    ? <SortIcon className={'icon medium blurred'}/>
                                                    : sortStates[g.status.id] === 1
                                                        ? <SortIcon className={'icon medium'}/>
                                                        : <SortIcon className={'icon medium flipped'}/>
                                            }

                                        </IconButton>
                                    </div>
                                    <SortableTaskColumns
                                        list={g.tasks}
                                        statusId={g.status.id}
                                        groupName={sortableGroupName}
                                        onEnd={onNoteDragged}
                                        disabled={searchData.loading || movingATask}
                                        onEditTask={onEditTask}
                                        onRemoveTask={onRemoveTask}
                                        toggleTaskDoneStatus={toggleTaskDoneStatus}
                                        setTask={setTask}
                                    />
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
            <UpsertTaskStatusesDialog
                open={statusesDialogState}
                close={() => setStatusesDialogState(false)}
                onFinish={setStatuses}
            />
        </>
    );
}

export default NotebookView;
